import {Component, ChangeDetectionStrategy, computed, input} from '@angular/core';

/** What a sync's status word says, and its badge colour. */
const STATUS: Record<string, { label: string; badge: string }> = {
  scheduled: {label: 'Scheduled', badge: 'text-bg-secondary'},
  waiting: {label: 'Waiting', badge: 'text-bg-warning'},
  pending: {label: 'Running', badge: 'text-bg-info'},
  finished: {label: 'Synced', badge: 'text-bg-success'},
  failed: {label: 'Failed', badge: 'text-bg-danger'},
};

/** One line of the cell: a sync before or after the launch. */
export interface CampaignApiSyncLine {
  phase: 'Before' | 'After';
  name: string;
  status: string;
  badge: string;
  /** Why it failed, or what it waits for. */
  tooltip: string;
}

/**
 * The registration platform syncs a scheduled launch runs (the campaigns list's API Sync column):
 * before the launch and after it, from the list's `include_sync_influencers_details` fields.
 */
export function campaignApiSyncLines(campaign: any): CampaignApiSyncLine[] {
  const lines: CampaignApiSyncLine[] = [];
  const before = campaign?.sync_influencers_details;
  if (campaign?.sync_influencers_before_launch && before?.sync_registration_platform) {
    // A launch cancelled over its sync failed, whatever the sync's own status says.
    const status = before.launch_cancelled_due_to_sync_failure ? 'failed' : (before.pre_launch_sync_status || 'scheduled');
    lines.push(lineOf('Before', before.sync_registration_platform_name || before.sync_registration_platform, status,
      status === 'failed' ? before.sync_failure_message : ''));
  }
  const after = campaign?.sync_influencers_after_launch_details;
  if (campaign?.sync_influencers_after_launch && after) {
    const status = after.status || 'scheduled';
    const tooltip = status === 'failed' ? after.failure_message : (status === 'waiting' ? after.waiting_reason : '');
    lines.push(lineOf('After', after.name || after.sync_registration_platform, status, tooltip));
  }
  return lines;
}

function lineOf(phase: 'Before' | 'After', name: string, status: string, tooltip: string | null | undefined): CampaignApiSyncLine {
  const known = STATUS[status];
  return {
    phase,
    name: name || 'API integration',
    status: known?.label || status,
    badge: known?.badge || 'text-bg-secondary',
    tooltip: tooltip || '',
  };
}

/** The API Sync cell of the campaigns list, in the list rows and on the cards. */
@Component({
  selector: 'app-campaign-api-sync',
  template: `
    @if (lines().length) {
      @for (line of lines(); track line) {
      <div class="campaign-api-sync__line" [attr.data-phase]="line.phase">
        <span class="campaign-api-sync__name">{{ line.phase }}: {{ line.name }}</span>
        ·
        <span class="badge h-auto campaign-api-sync__status" [class]="line.badge"
              [attr.title]="line.tooltip || null">{{ line.status }}</span>
      </div>
      }
    } @else {
    <span class="campaign-api-sync__none">—</span>
    }
  `,
  styles: [`
    :host {
      display: block;
    }

    .campaign-api-sync__line {
      white-space: nowrap;
    }

    .campaign-api-sync__line + .campaign-api-sync__line {
      margin-top: 0.25rem;
    }

    /* The card view shows every span of a card as a block. */
    .campaign-api-sync__name {
      display: inline !important;
    }

    .campaign-api-sync__status {
      display: inline-block !important;
    }

    .campaign-api-sync__status[title] {
      cursor: help;
    }

    .campaign-api-sync__none {
      color: var(--text-secondary, #7a7b97);
    }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CampaignApiSyncComponent {
  readonly campaign = input<any>(undefined);
  readonly lines = computed(() => campaignApiSyncLines(this.campaign()));
}
